import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { CITIES } from "@/siteData";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Interior Painting",
  "Exterior Painting",
  "Drywall Repair & Finishing",
  "Multiple Services / Not Sure",
];

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20";

export const EstimateForm = ({ defaultService = "", defaultCity = "" }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: defaultService,
    city: defaultCity,
    message: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/estimate-requests`, form);
      trackEvent("generate_lead", { service: form.service, city: form.city });
      setStatus("sent");
      toast.success("Estimate request sent — we'll be in touch soon.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong sending your request. Please call 913-244-4798.");
    }
  };

  if (status === "sent") {
    return (
      <div
        data-testid="estimate-form-success"
        className="rounded-2xl border border-amber-600/30 bg-amber-50 p-8 text-center"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-amber-600" />
        <h3 className="mt-4 font-display text-xl font-bold text-slate-900">
          Request received
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          Thanks, {form.name.split(" ")[0]} — we've got your estimate request and
          will reach out shortly. Prefer to talk now? Call{" "}
          <a href="tel:+19132444798" className="font-semibold text-amber-700">
            913-244-4798
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="estimate-form" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="estimate-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
            Name *
          </label>
          <input
            id="estimate-name"
            data-testid="estimate-form-name-input"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="estimate-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
            Email *
          </label>
          <input
            id="estimate-email"
            data-testid="estimate-form-email-input"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="you@email.com"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="estimate-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
            Phone
          </label>
          <input
            id="estimate-phone"
            data-testid="estimate-form-phone-input"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="(913) 555-0100"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="estimate-city" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
            City
          </label>
          <select
            id="estimate-city"
            data-testid="estimate-form-city-select"
            value={form.city}
            onChange={set("city")}
            className={inputCls}
          >
            <option value="">Select your city</option>
            {CITIES.map((c) => (
              <option key={c.slug} value={`${c.city}, ${c.state}`}>
                {c.city}, {c.state}
              </option>
            ))}
            <option value="Other KC metro">Other (KC metro)</option>
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="estimate-service" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
          Service needed
        </label>
        <select
          id="estimate-service"
          data-testid="estimate-form-service-select"
          value={form.service}
          onChange={set("service")}
          className={inputCls}
        >
          <option value="">Select a service</option>
          {SERVICE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="estimate-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
          Project details
        </label>
        <textarea
          id="estimate-message"
          data-testid="estimate-form-message-input"
          rows={4}
          value={form.message}
          onChange={set("message")}
          placeholder="Tell us about your project — rooms, square footage, timeline, anything that helps."
          className={inputCls}
        />
      </div>
      <button
        type="submit"
        data-testid="estimate-form-submit"
        disabled={status === "sending"}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-600 px-6 py-3.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:scale-[1.02] hover:bg-amber-700 disabled:opacity-60 sm:w-auto"
      >
        <Send className="h-4 w-4" />
        {status === "sending" ? "Sending…" : "Request My Free Estimate"}
      </button>
    </form>
  );
};
